import { Button, ButtonType } from '@ardoq/button';
import { AddIcon, MoreVertIcon } from '@ardoq/icons';
import { ButtonWithDropdown } from '@ardoq/dropdown-menu';
import { FlexBox } from '@ardoq/layout';
import { SearchColumnGroup } from './SearchColumnGroup';
import { VisualizeButton } from './VisualizeButton';
import {
  BrowseComponentsInteractiveRowViewState,
  InventoryInteractiveTopRowCommands,
} from './types';
import { CreateComponentFromSurveyButton } from './CreateComponentFromSurveyButton';
import { type I18nLocale, t } from '@ardoq/i18n';
import { HierarchyViewControls } from './HierarchyViewControls';
import { inventoryDatasourceOperations } from '../datasourceOperations';
import { tourAnchorIds } from '@ardoq/product-tours';
import { TableControlsInteractiveRow } from './TableControlsInteractiveRow';

type BrowseComponentsInteractiveRowProps = {
  commands: InventoryInteractiveTopRowCommands;
  state: BrowseComponentsInteractiveRowViewState;
  i18nLocale: I18nLocale;
};

export const BrowseComponentsInteractiveRow = ({
  commands,
  state,
  i18nLocale,
}: BrowseComponentsInteractiveRowProps) => {
  return (
    <TableControlsInteractiveRow i18nLocale={i18nLocale}>
      {/* group 1: search, view toggle - left */}
      <FlexBox gap="xsmall" align="center" flex={1}>
        <SearchColumnGroup
          state={state}
          commands={commands}
          i18nLocale={i18nLocale}
        />
        {state.showHierarchyViewControls && (
          <HierarchyViewControls
            isHierarchyEnabled={state.isHierarchyEnabled}
            datasourceSelection={state.datasourceSelection}
            onToggleHierarchy={commands.toggleHierarchy}
            onBackToComponentType={commands.goBackToComponentType}
            i18nLocale={i18nLocale}
          />
        )}
      </FlexBox>

      {/* group 2: action buttons - right */}
      <FlexBox gap="xsmall" align="center">
        <FlexBox
          gap="xsmall"
          align="center"
          data-tour-id={tourAnchorIds.inventoryRowActions}
        >
          {state.shouldDisplayBulkEditOptions && (
            <ButtonWithDropdown
              buttonType={ButtonType.TERTIARY}
              hideChevron
              options={state.moreButtonOptions}
              aria-label={t(
                i18nLocale,
                'inventory:interactiveRow.moreActions',
                'More actions'
              )}
            >
              <MoreVertIcon />
            </ButtonWithDropdown>
          )}
          {state.shouldDisplayCreateComponentButton && (
            <Button
              onClick={() =>
                commands.enterCreateComponentsMode({
                  shouldAskToPasteValues: true,
                })
              }
              isDisabled={!state.canCreateComponent}
              data-tour-id={tourAnchorIds.inventoryCreateButton}
            >
              <AddIcon />
              {t(i18nLocale, 'inventory:interactiveRow.create', 'Create')}
            </Button>
          )}
          {state.shouldDisplayCreateComponentsThroughSurveysButton && (
            <CreateComponentFromSurveyButton
              openSurvey={commands.openSurvey}
              surveys={state.relevantSurveys}
              workspaceNameBySurveyId={state.workspaceNameBySurveyId}
              i18nLocale={i18nLocale}
            />
          )}
          {inventoryDatasourceOperations.isDatasetInventoryDatasourceSelection(
            state.datasourceSelection
          ) ? (
            // This is temporary and should be moved into a separate component
            // to reduce duplication when this is no longer a POC
            <Button
              onClick={commands.goBackToVisualization}
              buttonType={ButtonType.PRIMARY}
            >
              {t(
                i18nLocale,
                'inventory:interactiveRow.backToVisualization',
                'Back to Visualization'
              )}
            </Button>
          ) : (
            <VisualizeButton
              selectedCount={0}
              visualizeButtonOptions={state.visualizeButtonOptions}
              totalNumberOfRows={state.totalNumberOfMatchingRows}
              isUserContributor={state.isUserContributor}
              // no-op until browse-mode visualization alpha is implemented
              openOverviewViewpoint={() => {}}
              i18nLocale={i18nLocale}
            />
          )}
        </FlexBox>
      </FlexBox>
    </TableControlsInteractiveRow>
  );
};
